import { assert, runInDebug } from '@ember/debug';
import { getOwner } from '@ember/application';
import Service from '@ember/service';

/**
 * SPDS Menu Service
 *
 * Owns the single `document` click listener that dismisses open SPDS menus, replacing the
 * per-instance listener the `close-on-click-outside` modifier used to attach. One listener
 * serves every registered menu, and `event.composedPath()` is computed once per click instead
 * of once per instance.
 *
 * Registration is lifecycle-driven, not open-driven: `Spds::Core::KeyboardNavigable` registers
 * itself from `setup()` and unregisters on teardown, and the click handler reads `open` off each
 * registered instance at click time. Nothing has to tell the service when a menu opens, so no
 * per-template modifier is needed, and close handlers no longer run for menus that are closed.
 *
 * A *dismissable* is anything exposing:
 *
 * - `open` - whether it should participate in dismissal right now
 * - `baseElement` - the containment boundary; a click inside it is not "outside"
 * - `dismissSelector` - optional class name that dismisses even when clicked inside the boundary
 *   (used by `Spds::Input::Search` and `Spds::Input::Dropdown::List` for their labels)
 * - `handleClose()` - called when a click lands outside the boundary
 *
 * SPDS menu components satisfy that contract natively. The `close-on-click-outside` modifier
 * supplies an object literal for the app-side call sites that are not menu components.
 *
 * Note: this service is `spds/menu`, which reads like the `Spds::Menu::*` components. Ember keeps
 * the service and component registries separate, so there is no resolver collision.
 */
export default class SpdsMenuService extends Service {
  /**
   * Registered dismissables. Deliberately untracked: it is written from component lifecycle hooks
   * and never read during render, so tracking it would buy nothing and risk a backtracking
   * assertion. Open-ness is resolved from the instances at click time, not from this set.
   * @private
   */
  #dismissables = new Set();

  /** @private */
  #listening = false;

  /**
   * Resolved once here rather than injected or looked up on demand, because both alternatives throw.
   * `@service fastboot` is validated on instantiation and blows up in any build without
   * `ember-cli-fastboot` registered, while looking it up lazily blows up the other way: the
   * `unregisterMenu` destructor can run after the owner is destroyed, and `lookup` is illegal by
   * then. The constructor always runs while the owner is alive, so resolving here is safe.
   * @private
   */
  #isFastBoot = false;

  constructor() {
    super(...arguments);

    this.#isFastBoot = getOwner(this)?.lookup('service:fastboot')?.isFastBoot ?? false;
  }

  /**
   * the registered dismissables that a click should currently be able to close
   *
   * The destroy check matters because the registry holds instances: a component torn down without
   * its destructor running (or mid-teardown) must not be dispatched to.
   *
   * @return {Array<object>}
   */
  get openMenus() {
    return [...this.#dismissables].filter(
      menu => menu.open && !menu.isDestroying && !menu.isDestroyed
    );
  }

  /**
   * Registers a dismissable. Called once per instance from `Spds::Core::KeyboardNavigable.setup`
   * and from the `close-on-click-outside` modifier.
   *
   * @param {object} dismissable - exposes `open`, `baseElement`, `dismissSelector`, `handleClose`
   */
  registerMenu = dismissable => {
    runInDebug(() => {
      assert(
        'spds/menu: registerMenu expects a dismissable exposing a `handleClose` function',
        typeof dismissable?.handleClose === 'function'
      );
    });

    this.#dismissables.add(dismissable);
    this.#syncListener();
  };

  /**
   * Removes a dismissable from the registry, detaching the listener once nothing is left.
   *
   * @param {object} dismissable - the instance or adapter passed to `registerMenu`
   */
  unregisterMenu = dismissable => {
    this.#dismissables.delete(dismissable);
    this.#syncListener();
  };

  willDestroy() {
    super.willDestroy(...arguments);

    this.#dismissables.clear();
    this.#syncListener();
  }

  /**
   * Attaches the listener while anything is registered and detaches it once the registry empties,
   * so a torn-down test app leaves no listener on `document`. No-ops when the desired state already
   * matches, and under FastBoot where there is no `document` to listen on.
   * @private
   */
  #syncListener = () => {
    if (this.#isFastBoot) return;

    let shouldListen = this.#dismissables.size > 0;

    if (shouldListen === this.#listening) return;

    if (shouldListen) {
      document.addEventListener('click', this.#onDocumentClick);
    } else {
      document.removeEventListener('click', this.#onDocumentClick);
    }

    this.#listening = shouldListen;
  };

  /**
   * @private
   */
  #onDocumentClick = event => {
    // Bail before touching `composedPath()`, which allocates an array of the full DOM path. With
    // nothing open this is a handful of boolean reads per click.
    let openMenus = this.openMenus;

    if (!openMenus.length) return;

    let { target } = event;
    // Resolve containment from the event's composed path (captured at dispatch time)
    // so a click that originated inside the element is still treated as "inside" even
    // if the clicked node (e.g. a conditionally rendered clear button) is removed from
    // the DOM during the same click. Falls back to `contains` for older event targets.
    let path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    // A listener per instance used to give each menu error isolation for free, since the browser
    // catches per listener. With one shared listener a throwing consumer `onClose` would otherwise
    // stop every menu after it from dismissing, so the first failure is held and rethrown at the
    // end - out of the listener, where the browser reports it exactly as it did before.
    let failure;

    // `openMenus` is a fresh array, so it is the snapshot: a handler that unregisters its own menu
    // (or another's) mid-iteration does not disturb the loop.
    for (let menu of openMenus) {
      let element = menu.baseElement;

      if (!element) continue;

      let clickedInside = path.includes(element) || element.contains(target);

      if (clickedInside && !target.classList?.contains(menu.dismissSelector)) continue;

      try {
        menu.handleClose();
      } catch (error) {
        failure ??= error;
      }
    }

    if (failure) throw failure;
  };
}
